import { SplashScreen } from '@capacitor/splash-screen';
import * as Sentry from '@sentry/angular';
import { Startup } from 'src/app/core/contracts';
import { isCapacitor } from 'src/app/core/helpers/utils';

/**
 * Native splash safety net.
 *
 * `launchAutoHide` is off (capacitor.config.ts), so the native splash — a solid brand-green image — stays up until the
 * app hides it. Historically only SplashPage / SignupPage did, which meant any startup failure before those pages
 * constructed (a hung or rejected bootstrap, a route guard that never resolves, a broken lazy chunk) left users on a
 * green screen forever, with nothing reported ("green-screen bug"). The watchdog armed from main.ts is deliberately
 * independent of the Angular bootstrap chain: it is only disarmed by an actual hide — never by "bootstrap resolved",
 * because the router's initial navigation, the route guards and the lazy chunks all run *after* that.
 *
 * The retry UI is built here, framework-free: Angular and i18n are exactly what is unavailable when it is needed, hence
 * both languages inline. It is shown only when nothing rendered, and removed again by the next hideNativeSplash() call
 * so it can never cover a late-rendering app.
 */

let hidden = false;
let watchdog: ReturnType<typeof setTimeout> | undefined;
let fallback: HTMLElement | undefined;

/** Hides the native splash (idempotent, best effort — a no-op on web) and disarms the watchdog. Called by every entry page. */
export function hideNativeSplash() {
  removeBootFallback(); // a page is rendering — the retry UI must never cover it
  if (hidden) {
    return;
  }
  hidden = true;
  clearTimeout(watchdog);
  SplashScreen.hide().catch(() => {});
}

/** Last-resort guarantee that the native splash comes down; if nothing rendered by then, reports and offers the retry UI.
 *  Native only: the web build has no native splash, and a slow deep-linked page there must never end up behind the retry UI
 *  (only SplashPage / SignupPage remove it, and neither renders on a direct URL). */
export function armNativeSplashWatchdog(ms = Startup.NATIVE_SPLASH_WATCHDOG_MS) {
  if (!isCapacitor) {
    return;
  }
  clearTimeout(watchdog);
  watchdog = setTimeout(() => {
    if (hidden) {
      return;
    }
    if (hasRenderedPage()) {
      // a page rendered without passing through SplashPage / SignupPage (e.g. the web login redirect or a direct URL
      // lands straight on its target route) — nothing is wrong, just make sure the splash is down
      hideNativeSplash();
      return;
    }
    const msg = `@splash.fallback: nothing rendered within ${ms}ms, forcing the native splash down`;
    console.warn(msg);
    Sentry.captureMessage(msg, 'error');
    hideNativeSplash();
    showBootFallback();
  }, ms);
}

/** `bootstrapApplication(...).catch` handler: report, bring the splash down, offer a retry. */
export function onBootstrapFailure(err: unknown) {
  // eslint-disable-next-line no-console
  console.error(err);
  Sentry.captureException(err);
  hideNativeSplash();
  showBootFallback();
}

function showBootFallback() {
  if (fallback) {
    return;
  }
  fallback = buildBootFallback();
  document.body.appendChild(fallback);
}

function removeBootFallback() {
  fallback?.remove();
  fallback = undefined;
}

function hasRenderedPage(): boolean {
  const outlet = document.querySelector('ion-router-outlet');
  return !!outlet && outlet.children.length > 0;
}

function buildBootFallback(): HTMLElement {
  const root = document.createElement('div');
  root.id = 'boot-fallback';
  Object.assign(root.style, {
    position: 'fixed',
    top: '0',
    right: '0',
    bottom: '0',
    left: '0',
    zIndex: '2147483647',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    padding: '24px',
    background: '#fff',
    color: '#0f423d', // --ion-color-primary
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    textAlign: 'center',
  });
  root.append(message('تعذّر تشغيل التطبيق', 'ar', 'rtl'), message('The app could not start', 'en', 'ltr'));

  const retry = document.createElement('button');
  retry.type = 'button';
  retry.textContent = 'إعادة المحاولة · Retry';
  Object.assign(retry.style, {
    marginTop: '12px',
    padding: '12px 32px',
    border: '0',
    borderRadius: '24px',
    background: '#0f423d',
    color: '#fff',
    fontSize: '16px',
  });
  retry.addEventListener('click', () => window.location.reload(), { once: true });
  root.appendChild(retry);
  return root;
}

function message(text: string, lang: string, dir: 'ltr' | 'rtl'): HTMLParagraphElement {
  const paragraph = document.createElement('p');
  paragraph.lang = lang;
  paragraph.dir = dir;
  paragraph.textContent = text;
  Object.assign(paragraph.style, { margin: '0', fontSize: '18px' });
  return paragraph;
}
